import { Link } from '@tanstack/react-router'
import { HighlightParts, SuggestItem } from 'lib/suggest-groups'

type Props = {
   /** The rows to draw, already capped — see MAX_SUGGEST_ROWS. */
   items: SuggestItem[]
   /**
    * The row ⏎ would open — moved onto with ↑↓ — or -1 when nothing is picked
    * and ⏎ should stay an ordinary newline.
    */
   selectedIndex: number
   /** The row under the pointer, or -1. Marked, but never the ⏎ target. */
   hoveredIndex: number
   onHover: (index: number) => void
   onPick: (index: number) => void
   listboxId: string
   optionId: (index: number) => string
}

/**
 * The matched run of a string. The field the query landed in carries the ink:
 * its surroundings drop back to muted so the matched run reads as the answer,
 * rather than every row shouting its whole headword at once.
 */
const Highlighted = ({ parts, strong }: { parts: HighlightParts; strong: string }) => {
   if (!parts.mid) return <>{parts.pre}</>
   return (
      <>
         {parts.pre}
         <b className={`font-semibold ${strong}`}>{parts.mid}</b>
         {parts.post}
      </>
   )
}

const SuggestRow = ({
   item,
   index,
   isSelected,
   isMarked,
   onHover,
   onPick,
   optionId,
}: {
   item: SuggestItem
   index: number
   isSelected: boolean
   /** Under the keyboard cursor or the pointer — either way, worth marking. */
   isMarked: boolean
   onHover: (index: number) => void
   onPick: (index: number) => void
   optionId: string
}) => (
   // A real anchor, as the search-result lists are: cmd/middle-click and "open
   // in new tab" have to keep working, which a div with an onClick would break.
   <Link
      {...item.route}
      id={optionId}
      role="option"
      aria-selected={isSelected}
      className="flex items-baseline gap-[16px] pt-[11px] pr-[4px] pb-[12px] pl-[22px] border-b border-hairline no-underline text-inherit cursor-pointer"
      onMouseEnter={() => onHover(index)}
      onClick={() => onPick(index)}
   >
      <div className="flex-1 min-w-0 flex flex-wrap sm:flex-nowrap items-baseline gap-x-[16px] gap-y-[2px]">
         <span
            className={`flex-none leading-[1.3] ${
               item.isThaiHeadword ? 'font-thai text-[22px]' : 'font-latin text-[20px]'
            } ${item.head.mid ? 'text-textMuted' : 'text-bodyBlack'}`}
         >
            <Highlighted parts={item.head} strong="text-bodyBlack" />
         </span>
         {item.translit.pre || item.translit.mid ? (
            <span
               className={`flex-none font-translit text-[16px] italic ${
                  item.translit.mid ? 'text-textMuted' : 'text-translit'
               }`}
            >
               <Highlighted parts={item.translit} strong="text-translit" />
            </span>
         ) : null}
         <span
            className={`w-full min-w-0 sm:w-auto sm:flex-1 text-[16px] text-bodyGray whitespace-nowrap overflow-hidden text-ellipsis ${
               item.isThaiHeadword ? 'font-latin' : 'font-thai'
            }`}
         >
            {item.meaning.pre}
            {item.meaning.mid}
            {item.meaning.post}
         </span>
      </div>
      <span
         aria-hidden="true"
         className={`material-icons flex-none text-[19px] ${
            isMarked ? 'text-translit' : 'text-transparent'
         }`}
      >
         arrow_forward
      </span>
   </Link>
)

/**
 * What the dictionary has for whatever is in the homepage box, docked straight
 * underneath it rather than floating over the page.
 *
 * The homepage box is a translation box first, so the lookup sits *beside* that
 * job instead of interrupting it: no card, no backdrop, no dimming — a short
 * flat list that grows out of the box and says plainly which of the two things
 * ⏎ is about to do.
 *
 * Rows or nothing. There is no line for a miss and none for a phrase: the box
 * is a translation box already, so telling someone mid-sentence that Translate
 * will take it from here interrupts typing to say what the button says.
 *
 * Presentational only. Selection lives on the page, because the keyboard that
 * drives it belongs to the textarea.
 */
const HomepageSuggestList = ({
   items,
   selectedIndex,
   hoveredIndex,
   onHover,
   onPick,
   listboxId,
   optionId,
}: Props) => {
   const selected = selectedIndex >= 0 ? items[selectedIndex] : undefined

   return (
      <div className="mt-[20px]">
         {items.length > 0 && (
            <>
               <div id={listboxId} role="listbox" className="flex flex-col">
                  {items.map((item, index) => (
                     <SuggestRow
                        key={item.route.to + item.word}
                        item={item}
                        index={index}
                        isSelected={index === selectedIndex}
                        isMarked={index === selectedIndex || index === hoveredIndex}
                        onHover={onHover}
                        onPick={onPick}
                        optionId={optionId(index)}
                     />
                  ))}
               </div>
               {/* Says what ⏎ does right now, which is the whole reason nothing
                   is picked to begin with: until ↑↓ moves onto a row, ⏎ is
                   still the newline the box has always had, and a list that
                   quietly took it over would eat the second line of anything
                   typed in here. */}
               <div className="pt-[9px] pr-[4px] pl-[22px] text-[13px] text-textMuted">
                  {selected ? (
                     <>
                        ⏎ opens{' '}
                        <b
                           className={`font-semibold text-bodyGray ${
                              selected.isThaiHeadword ? 'font-thai' : 'font-latin'
                           }`}
                        >
                           {selected.word}
                        </b>
                     </>
                  ) : (
                     <>↑↓ to pick a word</>
                  )}
               </div>
            </>
         )}
      </div>
   )
}

export default HomepageSuggestList
